/**
 * ValueIt — Dynamic Graphs ApexCharts theme
 *
 * Page-local counterpart to src/pages/Compare/chartTheme.ts. Used to be
 * fully self-contained literal hex, deliberately independent of the
 * app-wide --vi-* tokens — that changed once DynamicGraphs became the
 * source the app's "teal" palette was extracted from (theme-tokens.css's
 * `[data-vi-palette="teal"]` blocks): the two series colors that most
 * read as "the accent" (slots 1-2) now come from var(--vi-chart-1)/
 * var(--vi-chart-2) so they track whichever palette is active, same as
 * every UI-chrome color in dynamicGraphs.theme.css. ApexCharts renders
 * these as inline SVG attributes at paint time, and a CSS custom
 * property resolves there exactly the way it does in a stylesheet rule
 * (confirmed working — this isn't a canvas-rendered chart).
 *
 * Slots 3-6 now also track the shared var(--vi-chart-3..6) tokens (on
 * request, for consistency — every other chart in the app, via
 * Compare/chartTheme.ts's viChartColors, already reads this exact same
 * 6-color set per palette). This page used to keep its own independent
 * cyan/teal literal-hex extension set here instead; that's gone, along
 * with the unused 7th/8th slots (the shared set only defines 6 — a 7th+
 * series just repeats from slot 1, same as everywhere else). */
import merge from "lodash/merge";
import type { ApexOptions } from "apexcharts";

export const dgChartColors: string[] = [
  "var(--vi-chart-1)",
  "var(--vi-chart-2)",
  "var(--vi-chart-3)",
  "var(--vi-chart-4)",
  "var(--vi-chart-5)",
  "var(--vi-chart-6)",
];

/** Every chart on this page offsets its data labels ABOVE the point/bar
 *  (`offsetY: -10` for line/area, `-20` for bar — see
 *  newDynamicGraph.tsx), so the label floats over the PAGE background
 *  rather than sitting on top of a colored bar fill. var(--vi-text) is
 *  correct in both modes here (near-white on the dark page, near-black
 *  on the light one) — unlike a fixed light color, which only worked
 *  while this page was dark-only. */
const dataLabelOnMarkColor = "var(--vi-text)";

const dgBaseOptions: ApexOptions = {
  colors: dgChartColors,
  chart: {
    background: "transparent",
    foreColor: "var(--vi-text-tertiary)",
    fontFamily: "Inter, sans-serif",
    toolbar: { show: false },
    animations: {
      enabled: true,
      easing: "easeinout",
      speed: 800,
      animateGradually: { enabled: true, delay: 150 },
      dynamicAnimation: { enabled: true, speed: 400 },
    },
  },
  stroke: {
    curve: "smooth",
    width: 2.5,
  },
  /* Markers stay invisible until hover (size:0, matching the prior
     behavior) — only the hover ring's color needed fixing: ApexCharts'
     default stroke there is white, which read as a stray white dot on
     the dark palette. var(--vi-card-bg) makes it look like a "cut-out"
     ring in the mark's own color instead, in either mode. */
  markers: {
    size: 0,
    strokeWidth: 2,
    strokeColors: "var(--vi-card-bg)",
    hover: { size: 5 },
  },
  grid: {
    borderColor: "var(--vi-card-border)",
    strokeDashArray: 3,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
    padding: { top: 0, right: 8, bottom: 0, left: 8 },
  },
  xaxis: {
    axisBorder: { show: false },
    axisTicks: { show: false },
    crosshairs: {
      show: true,
      stroke: { color: "var(--vi-card-border)", width: 1, dashArray: 3 },
    },
    labels: {
      style: { colors: "var(--vi-text-tertiary)", fontSize: "11px", fontFamily: "Inter, sans-serif" },
    },
  },
  yaxis: {
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      style: { colors: "var(--vi-text-tertiary)", fontSize: "11px", fontFamily: "Inter, sans-serif" },
    },
  },
  tooltip: {
    theme: "dark",
    followCursor: true,
    marker: { show: true },
  },
  dataLabels: {
    style: {
      colors: [dataLabelOnMarkColor],
      fontSize: "10px",
      fontFamily: "Inter, sans-serif",
      fontWeight: 600,
    },
    background: { enabled: false },
  },
  legend: {
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    labels: { colors: "var(--vi-text-secondary)" },
    markers: { width: 8, height: 8, radius: 8 },
    itemMargin: { horizontal: 10, vertical: 4 },
  },
};

const dgBarPlotOptions: ApexOptions = {
  plotOptions: {
    bar: {
      borderRadius: 4,
      borderRadiusApplication: "end",
      columnWidth: "45%",
      dataLabels: { position: "top" },
    },
  },
  /* Vertical gradient instead of a flat fill — fading OPACITY of the
     bar's own color from top to bottom, not shifting its HUE, which is
     the actual "glass" look (a tinted pane gets more see-through, it
     doesn't turn grey). Explicit `gradientToColors: dgChartColors`
     rather than ApexCharts' `shade`/`shadeIntensity` auto-derivation —
     that auto mode computes the second stop with its OWN internal color
     math, which can't parse a "var(--vi-chart-1)" string as a real
     color and silently fell back to a dark/grey stop. Using the same
     color string for both stops sidesteps that math entirely (both
     stops just resolve the CSS variable directly, the same way a plain
     `fill` color already does), and reads correctly against either a
     dark or light page. */
  fill: {
    type: "gradient",
    gradient: {
      type: "vertical",
      gradientToColors: dgChartColors,
      inverseColors: false,
      opacityFrom: 0.95,
      opacityTo: 0.35,
      stops: [0, 100],
    },
  },
  states: {
    hover: { filter: { type: "lighten", value: 0.1 } },
  },
};

const dgAreaFillOptions: ApexOptions = {
  /* Same fix as the bar fill above: explicit gradientToColors instead of
     auto-shade, so the fade is pure opacity (this page's own color,
     fading transparent) rather than drifting toward the broken
     auto-derived dark/grey stop that "var(--vi-chart-*)" produced. */
  fill: {
    type: "gradient",
    gradient: {
      gradientToColors: dgChartColors,
      opacityFrom: 0.45,
      opacityTo: 0.04,
      stops: [0, 90, 100],
    },
  },
};

export const mergeDgChartOptions = (overrides: ApexOptions): ApexOptions =>
  merge({}, dgBaseOptions, overrides);

export const mergeDgBarChartOptions = (overrides: ApexOptions): ApexOptions =>
  merge({}, dgBaseOptions, dgBarPlotOptions, overrides);

export const mergeDgAreaChartOptions = (overrides: ApexOptions): ApexOptions =>
  merge({}, dgBaseOptions, dgAreaFillOptions, overrides);
